import type { ReactNode, SVGProps } from "react";

type IconProps = SVGProps<SVGSVGElement>;

function BaseIcon({ children, ...props }: IconProps & { children: ReactNode }) {
  return (
    <svg
      width="1em"
      height="1em"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      {...props}
    >
      {children}
    </svg>
  );
}

/** Sidebar / card icons — local SVG so app code never imports @mui/icons-material. */
export function HomeOutlinedIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M12 5.69 17 10.19V18h-2v-6H9v6H7v-7.81l5-4.5M12 3 2 12h3v8h6v-6h2v6h6v-8h3L12 3z" />
    </BaseIcon>
  );
}

export function BusinessOutlinedIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z" />
    </BaseIcon>
  );
}

export function PeopleOutlinedIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M9 13.75c-2.34 0-7 1.17-7 3.5V19h14v-1.75c0-2.33-4.66-3.5-7-3.5zM4.34 17c.84-.58 2.87-1.25 4.66-1.25s3.82.67 4.66 1.25H4.34zM9 12c1.93 0 3.5-1.57 3.5-3.5S10.93 5 9 5 5.5 6.57 5.5 8.5 7.07 12 9 12zm0-5c.83 0 1.5.67 1.5 1.5S9.83 10 9 10s-1.5-.67-1.5-1.5S8.17 7 9 7zm7.04 6.81c1.16.84 1.96 1.96 1.96 3.44V19h4v-1.75c0-2.02-3.5-3.17-5.96-3.44zM15 12c1.93 0 3.5-1.57 3.5-3.5S16.93 5 15 5c-.54 0-1.04.13-1.5.35.63.89 1 1.98 1 3.15s-.37 2.26-1 3.15c.46.22.96.35 1.5.35z" />
    </BaseIcon>
  );
}

export function SettingsOutlinedIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M19.14 12.94c.04-.31.06-.63.06-.94 0-.31-.02-.63-.06-.94l2.03-1.58a.49.49 0 0 0 .12-.61l-1.92-3.32a.49.49 0 0 0-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54a.484.484 0 0 0-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94l-2.03 1.58a.49.49 0 0 0-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" />
    </BaseIcon>
  );
}

export function AccountTreeOutlinedIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M22 11V3h-7v3H9V3H2v8h7V8h2v10h4v3h7v-8h-7v3h-2V8h2v3h7zM7 9H4V5h3v4zm10 6h3v4h-3v-4zm0-10h3v4h-3V5z" />
    </BaseIcon>
  );
}

export function ArchiveOutlinedIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="m20.54 5.23-1.39-1.68C18.88 3.21 18.47 3 18 3H6c-.47 0-.88.21-1.16.55L3.46 5.23C3.17 5.57 3 6.02 3 6.5V19c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6.5c0-.48-.17-.93-.46-1.27zM6.24 5h11.52l.81.97H5.44l.8-.97zM5 19V8h14v11H5zm8.45-9h-2.9v3H8l4 4 4-4h-2.55z" />
    </BaseIcon>
  );
}

export function EditOutlinedIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a.996.996 0 0 0 0-1.41l-2.34-2.34a.996.996 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
    </BaseIcon>
  );
}

export function MergeTypeOutlinedIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M17 20.41 18.41 19 15 15.59 13.59 17 17 20.41zM7.5 8H11v5.59L5.59 19 7 20.41l6-6V8h3.5L12 3.5 7.5 8z" />
    </BaseIcon>
  );
}

export function SecurityOutlinedIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M12 1 3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z" />
    </BaseIcon>
  );
}

export function BadgeOutlinedIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M20 7h-5V4c0-1.1-.9-2-2-2h-2c-1.1 0-2 .9-2 2v3H4c-1.1 0-2 .9-2 2v11c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V9c0-1.1-.9-2-2-2zM9 12c.83 0 1.5.67 1.5 1.5S9.83 15 9 15s-1.5-.67-1.5-1.5S8.17 12 9 12zm3 6H6v-.43c0-.6.36-1.15.92-1.39.64-.28 1.34-.43 2.08-.43s1.44.15 2.08.43c.55.24.92.78.92 1.39V18zm1-9h-2V4h2v5zm5 7.5h-4V17h4v1.5zm0-3h-4V14h4v1.5z" />
    </BaseIcon>
  );
}

export function BlockOutlinedIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM4 12c0-4.42 3.58-8 8-8 1.85 0 3.55.63 4.9 1.69L5.69 16.9A7.902 7.902 0 0 1 4 12zm8 8c-1.85 0-3.55-.63-4.9-1.69L18.31 7.1A7.902 7.902 0 0 1 20 12c0 4.42-3.58 8-8 8z" />
    </BaseIcon>
  );
}

export function OpenInNewOutlinedIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M19 19H5V5h7V3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z" />
    </BaseIcon>
  );
}

export function AccountBalanceWalletOutlinedIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M21 7.28V5c0-1.1-.9-2-2-2H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-2.28c.59-.35 1-.98 1-1.72V9c0-.74-.41-1.37-1-1.72zM20 9v6h-7V9h7zM5 19V5h14v2h-6c-1.1 0-2 .9-2 2v6c0 1.1.9 2 2 2h6v2H5z" />
      <circle cx="16" cy="12" r="1.5" />
    </BaseIcon>
  );
}

export function AssignmentOutlinedIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M19 3h-4.18C14.4 1.84 13.3 1 12 1s-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm2 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" />
    </BaseIcon>
  );
}

export function GroupsOutlinedIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M12 12.75c1.63 0 3.07.39 4.24.9 1.08.48 1.76 1.56 1.76 2.73V18H6v-1.61c0-1.18.68-2.26 1.76-2.73 1.17-.52 2.61-.91 4.24-.91zM4 13c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm1.13 1.1c-.37-.06-.74-.1-1.13-.1-.99 0-1.93.21-2.78.58C.48 14.9 0 15.62 0 16.43V18h4.5v-1.61c0-.83.23-1.61.63-2.29zM20 13c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm4 3.43c0-.81-.48-1.53-1.22-1.85A6.95 6.95 0 0 0 20 14c-.39 0-.76.04-1.13.1.4.68.63 1.46.63 2.29V18H24v-1.57zM12 6c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3z" />
    </BaseIcon>
  );
}

export function PersonOutlinedIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
    </BaseIcon>
  );
}

export function MailOutlinedIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4-8 5-8-5V6l8 5 8-5v2z" />
    </BaseIcon>
  );
}

export function DescriptionOutlinedIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" />
    </BaseIcon>
  );
}

export function MenuBookOutlinedIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M21 5c-1.11-.35-2.33-.5-3.5-.5-1.95 0-4.05.4-5.5 1.5-1.45-1.1-3.55-1.5-5.5-1.5S2.45 4.9 1 6v14.65c0 .25.25.5.5.5.1 0 .15-.05.25-.05C3.1 20.45 5.05 20 6.5 20c1.95 0 4.05.4 5.5 1.5 1.35-.85 3.8-1.5 5.5-1.5 1.65 0 3.35.3 4.75 1.05.1.05.15.05.25.05.25 0 .5-.25.5-.5V6c-.6-.45-1.25-.75-2-.1zm0 13.5c-1.1-.35-2.3-.5-3.5-.5-1.7 0-4.15.65-5.5 1.5V8c1.35-.85 3.8-1.5 5.5-1.5 1.2 0 2.4.15 3.5.5v11.5z" />
    </BaseIcon>
  );
}

export function ChatOutlinedIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" />
    </BaseIcon>
  );
}

export function CloseOutlinedIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" />
    </BaseIcon>
  );
}

export function NotificationsOutlinedIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2zm-2 1H8v-6c0-2.48 1.51-4.5 4-4.5s4 2.02 4 4.5v6z" />
    </BaseIcon>
  );
}

export function OpenInFullOutlinedIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M21 11V3h-8l3.29 3.29-10 10L3 13v8h8l-3.29-3.29 10-10L21 11z" />
    </BaseIcon>
  );
}

export function CloseFullscreenOutlinedIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M22 3.41 16.71 8.7 20 12h-8V4l3.29 3.29L20.59 2 22 3.41zM3.41 22l5.29-5.29L12 20v-8H4l3.29 3.29L2 20.59 3.41 22z" />
    </BaseIcon>
  );
}

export function ArrowBackOutlinedIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
    </BaseIcon>
  );
}

export function InfoOutlinedIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M11 7h2v2h-2V7zm0 4h2v6h-2v-6zm1-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" />
    </BaseIcon>
  );
}

export function CheckCircleOutlinedIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1.29-4.29-3.54-3.54 1.41-1.41 2.12 2.12 4.24-4.24 1.41 1.41-5.64 5.66z" />
    </BaseIcon>
  );
}

export function DeleteOutlinedIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M16 9v10H8V9h8m-1.5-6h-5l-1 1H5v2h14V4h-3.5l-1-1zM18 7H6v12c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7z" />
    </BaseIcon>
  );
}

export function AddOutlinedIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
    </BaseIcon>
  );
}

export function ArrowUpwardOutlinedIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="m4 12 1.41 1.41L11 7.83V20h2V7.83l5.58 5.59L20 12l-8-8-8 8z" />
    </BaseIcon>
  );
}

export function ArrowDownwardOutlinedIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="m20 12-1.41-1.41L13 16.17V4h-2v12.17l-5.58-5.59L4 12l8 8 8-8z" />
    </BaseIcon>
  );
}

export function RefreshOutlinedIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M17.65 6.35A7.958 7.958 0 0 0 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0 1 12 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
    </BaseIcon>
  );
}

export function PeopleAltOutlinedIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M16.5 13c-1.2 0-3.22.69-3.22 2.06V18h6.44v-2.94c0-1.37-2.02-2.06-3.22-2.06zM12.04 10.6c1.38 0 2.5-1.12 2.5-2.5S13.42 5.6 12.04 5.6s-2.5 1.12-2.5 2.5 1.12 2.5 2.5 2.5zM7.76 13C6.56 13 4.54 13.69 4.54 15.06V18h4.9v-2.3c0-.6.16-1.54 1.02-2.2-.58-.3-1.3-.5-1.9-.5zm-.72-2.4c1.1 0 2-0.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2z" />
    </BaseIcon>
  );
}

export function WarningAmberOutlinedIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M12 5.99 19.53 19H4.47L12 5.99M12 2 1 21h22L12 2zm1 14h-2v2h2v-2zm0-6h-2v4h2v-4z" />
    </BaseIcon>
  );
}

export function HourglassEmptyOutlinedIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M6 2v6h.01L6 8.01 10 12l-4 4 .01.01H6V22h12v-5.99h-.01L18 16l-4-4 4-3.99-.01-.01H18V2H6zm10 14.5V20H8v-3.5l4-4 4 4zm-4-5-4-4V4h8v3.5l-4 4z" />
    </BaseIcon>
  );
}

export function PriorityHighOutlinedIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M10.01 21h3.98c.63 0 1.05-.64.84-1.22l-3.98-9.95c-.2-.5-.94-.5-1.14 0l-3.98 9.95c-.21.58.21 1.22.84 1.22zM12 17c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm1-5h-2V7h2v5z" />
    </BaseIcon>
  );
}

export function PrintOutlinedIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M19 8H5c-1.66 0-3 1.34-3 3v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3zm-3 11H8v-5h8v5zm3-7c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm-1-9H6v4h12V3z" />
    </BaseIcon>
  );
}

export function SendOutlinedIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M4.01 6.03 11.52 9.25 4 8.25l.01-2.22m7.51 8.72L4 17.97V15.75l7.51-1 8.99 3.98L3.5 2.98 22 12l-10.48 4.75z" />
    </BaseIcon>
  );
}

export function NotesOutlinedIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M3 18h12v-2H3v2zM3 6v2h18V6H3zm0 7h18v-2H3v2z" />
    </BaseIcon>
  );
}

export function TaskAltOutlinedIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M22 5.18 10.59 16.6l-4.24-4.24 1.41-1.41 2.83 2.83 10-10L22 5.18zM12 20c-4.41 0-8-3.59-8-8 0-1.57.46-3.03 1.24-4.26L3.7 6.3C2.64 7.99 2 9.93 2 12c0 5.52 4.48 10 10 10 2.07 0 4.01-.64 5.7-1.7l-1.44-1.44A7.927 7.927 0 0 1 12 20z" />
    </BaseIcon>
  );
}

export function ReplyOutlinedIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M10 9V5l-7 7 7 7v-4.1c5 0 8.5 1.6 11 5.1-1-5-4-10-11-11z" />
    </BaseIcon>
  );
}

export function AddReactionOutlinedIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M7 9.5C7 8.67 7.67 8 8.5 8s1.5.67 1.5 1.5S9.33 11 8.5 11 7 10.33 7 9.5zm5 0c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5-.67 1.5-1.5 1.5-1.5-.67-1.5-1.5zM12 17.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5zm10-4.5h-2v-2h-2v2h-2v2h2v2h2v-2h2v-2zm-2-7.5c0-1.1-.9-2-2-2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h12c1.1 0 2-.9 2-2V5.5zM20 16H5.17L4 17.17V6c0-.55.45-1 1-1h14c.55 0 1 .45 1 1v10z" />
    </BaseIcon>
  );
}

export function MoreVertOutlinedIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
    </BaseIcon>
  );
}

export function FlagOutlinedIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M14 6l-1-2H5v17h2v-7h5l1 2h7V6h-6zm4 8h-4l-1-2H7V6h5l1 2h5v6z" />
    </BaseIcon>
  );
}

export function MarkChatUnreadOutlinedIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M22 6.98V16c0 1.1-.9 2-2 2H6l-4 4V4c0-1.1.9-2 2-2h10.1c-.06.32-.1.66-.1 1s.04.68.1 1H4v12h16V7.9c.74-.15 1.42-.48 2-.92zM16 3c0 1.66 1.34 3 3 3s3-1.34 3-3-1.34-3-3-3-3 1.34-3 3z" />
    </BaseIcon>
  );
}

export function DoneAllOutlinedIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="m18 7-1.41-1.41-6.34 6.34 1.41 1.41L18 7zm4.24-1.41L11.66 16.17 7.48 12l-1.41 1.41L11.66 19l12-12-1.42-1.41zM.41 13.41 6 19l1.41-1.41L1.83 12 .41 13.41z" />
    </BaseIcon>
  );
}

export function DoneOutlinedIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z" />
    </BaseIcon>
  );
}

export function ClearOutlinedIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" />
    </BaseIcon>
  );
}
